import {
  Box,
  Button,
  Stack,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  InputAdornment,
  OutlinedInput,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Container,
  Toolbar,
  Divider,
  alpha,
} from '@mui/material';
import { useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import DashboardHeader from '@/@mui/dashboard/DashboardHeader';
// import { useCancelPendingRequest } from '@/api/endpoints/community/useCancelPendingRequest';
import { ApiResponse_Create_Community } from '@/api/endpoints/community/useCreateCommunity';
import {
  useGetCommunities,
} from '@/api/endpoints/community/useGetCommunities';
import {
  useGetPublicCommunities,
} from '@/api/endpoints/community/useGetPublicCommunities';
import { useJoinCommunity } from '@/api/endpoints/community/useJoinCommunity';
import { useLeaveCommunity } from '@/api/endpoints/community/useLeaveCommunity';
import { CalendarIcon } from '@/assets/icons/CalendarIcon';
import { EyeIcon } from '@/assets/icons/EyeIcon';
import { LogOut01Icon } from '@/assets/icons/LogOut01Icon';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { SearchMdIcon } from '@/assets/icons/SearchMdIcon';
import { UserPlus01Icon } from '@/assets/icons/UserPlus01Icon';
import { Users02Icon } from '@/assets/icons/Users02Icon';
import { PMPRoutes } from '@/config/routes';
import { useAuth } from '@/context/AuthProvider';

import CreateCommunityModal from './CreateCommunityModal';


interface CommunityMenuState {
  anchorEl: HTMLElement | null;
  communityId: number | null;
  communityName: string;
}

const MyCommunitiesPage: React.FC = () => {
  const theme = useTheme();
  const history = useHistory();
  const { session } = useAuth();
  const [menuState, setMenuState] = useState<CommunityMenuState>({
    anchorEl: null,
    communityId: null,
    communityName: '',
  });
  const [filterName, setFilterName] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [leaveConfirmDialogOpen, setLeaveConfirmDialogOpen] = useState(false);
  const [joiningCommunityId, setJoiningCommunityId] = useState<number | null>(null);
  // const [cancellingCommunityId, setCancellingCommunityId] = useState<number | null>(null);

  const { data: communities, error, isLoading, refetch } = useGetCommunities();
  const { data: publicCommunities, isLoading: publicCommunitiesLoading } =
    useGetPublicCommunities();
  const leaveCommunityMutation = useLeaveCommunity();
  const joinCommunityMutation = useJoinCommunity({
    onSuccess: () => {
      setJoiningCommunityId(null);
    },
    onError: () => {
      setJoiningCommunityId(null);
    },
  });
  // const cancelPendingMutation = useCancelPendingRequest({
  //   onSuccess: () => {
  //     setCancellingCommunityId(null);
  //   },
  //   onError: () => {
  //     setCancellingCommunityId(null);
  //   },
  // });

  const handleFilterName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterName(event.target.value);
  };

  const handleCreateCommunity = () => {
    setCreateModalOpen(true);
  };

  const handleCreateModalClose = () => {
    setCreateModalOpen(false);
  };

  const handleCommunityCreated = (community: ApiResponse_Create_Community) => {
    // Refresh the communities list
    refetch();
  };

  const handleViewCommunity = (communityId: number) => {
    history.push(PMPRoutes.communityView.replace(':id', communityId.toString()));
  };

  // const handleMenuOpen = (
  //   event: React.MouseEvent<HTMLElement>,
  //   communityId: number,
  //   communityName: string
  // ) => {
  //   event.stopPropagation();
  //   setMenuState({
  //     anchorEl: event.currentTarget,
  //     communityId,
  //     communityName,
  //   });
  // };

  const handleMenuClose = () => {
    setMenuState({
      anchorEl: null,
      communityId: null,
      communityName: '',
    });
  };

  const handleViewFromMenu = () => {
    if (menuState.communityId) {
      handleViewCommunity(menuState.communityId);
    }
    handleMenuClose();
  };

  const handleLeaveCommunity = () => {
    setLeaveConfirmDialogOpen(true);
    // Don't close menu here - keep menuState intact for the confirmation
  };

  const handleConfirmLeave = async () => {
    if (!menuState.communityId) return;

    try {
      await leaveCommunityMutation.mutateAsync({
        communityId: menuState.communityId,
      });

      setLeaveConfirmDialogOpen(false);
      // Close menu and clear state after successful leave
      handleMenuClose();
      // Refresh the communities list after leaving
      refetch();
    } catch (leaveError) {
      // Error handling is managed by the mutation
      // eslint-disable-next-line no-console
      console.error('Failed to leave community:', leaveError);
    }
  };

  const handleJoinCommunity = (communityId: number) => {
    setJoiningCommunityId(communityId);
    joinCommunityMutation.mutate({ communityId });
  };

  // const handleCancelPending = (communityId: number) => {
  //   setCancellingCommunityId(communityId);
  //   cancelPendingMutation.mutate({ communityId });
  // };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  const filteredCommunities = useMemo(() => {
    if (!communities) return [];

    return communities.filter(community =>
      community.name.toLowerCase().includes(filterName.toLowerCase())
    );
  }, [communities, filterName]);

  const filteredAvailableCommunities = useMemo(() => {
    if (!publicCommunities) return [];
    return publicCommunities.filter(community =>
      community.name.toLowerCase().includes(filterName.toLowerCase())
    );
  }, [publicCommunities, filterName]);

  const getAvatarProps = (name: string) => {
    const initials = name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .substring(0, 2);

    const colors = ['primary', 'secondary', 'error', 'warning', 'info', 'success'] as const;
    const colorIndex = name.length % colors.length;

    return {
      children: initials,
      sx: {
        bgcolor: `${colors[colorIndex]}.main`,
        color: 'white',
        width: 48,
        height: 48,
        fontSize: '1.125rem',
      },
    };
  };

  const communitiesCount = filteredCommunities.length;

  const hasCommunities = filteredCommunities.length > 0;
  const hasAvailableCommunities =
    publicCommunitiesLoading ||
    (Array.isArray(publicCommunities) && filteredAvailableCommunities.length > 0);
  const showDivider = hasCommunities && hasAvailableCommunities;
  const showEmptyState = !hasCommunities && !hasAvailableCommunities;

  return (
    <Box>
      <Container maxWidth="xl" sx={{ pt: { xs: 0.5, sm: 1 }, px: { xs: 1, sm: 2 } }}>
        <Stack direction="column" spacing={{ xs: 2, sm: 2.5 }}>
          {/* Header */}
          <DashboardHeader title={`Communities (${communitiesCount})`} />

          {/* Main Content Container */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: { xs: 1, sm: 2 },
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              overflow: 'hidden',
            }}
          >
            {/* Search Bar */}
            <Toolbar
              sx={{
                height: { xs: 56, sm: 64 },
                display: 'flex',
                justifyContent: 'space-between',
                p: { xs: 1.5, sm: theme.spacing(2) },
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'secondary.lighter',
              }}
            >
              <OutlinedInput
                fullWidth
                value={filterName}
                onChange={handleFilterName}
                placeholder="Search communities..."
                startAdornment={
                  <InputAdornment position="start">
                    <SearchMdIcon sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                }
                sx={{
                  maxWidth: { xs: 'calc(100% - 60px)', sm: 320 },
                  bgcolor: 'background.paper',
                }}
              />

              {/* Create Community Button */}
              <Button
                color="primary"
                variant="contained"
                startIcon={<PlusIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.25rem' } }} />}
                onClick={handleCreateCommunity}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  fontWeight: 500,
                  px: { xs: 1, sm: 1.5 },
                  py: { xs: 0.5, sm: 0.75 },
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  boxShadow: '0px 2px 8px rgba(157, 21, 0, 0.2)',
                  minWidth: { xs: 48, sm: 'auto' },
                  height: { xs: 40, sm: 40 },
                  '&:hover': {
                    bgcolor: 'primary.dark',
                    boxShadow: '0px 4px 12px rgba(157, 21, 0, 0.3)',
                  },
                  '& .MuiButton-startIcon': {
                    margin: { xs: 0, sm: 'inherit' },
                  },
                }}
              >
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Create Community</Box>
              </Button>
            </Toolbar>

            {/* Content Area */}
            <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'background.paper' }}>
              {isLoading && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Loading communities...
                  </Typography>
                </Box>
              )}

              {error && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="error">
                    Error loading communities: {error.message}
                  </Typography>
                </Box>
              )}

              {showEmptyState ? (
                <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 8 } }}>
                  <Users02Icon
                    sx={{ fontSize: { xs: 48, sm: 64 }, color: 'text.disabled', mb: 2 }}
                  />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {filterName
                      ? 'No communities found matching your search.'
                      : 'No communities found'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {filterName
                      ? 'Try searching with different keywords.'
                      : 'Create your first community to start connecting with other property managers.'}
                  </Typography>
                  {!filterName && (
                    <Button
                      variant="contained"
                      startIcon={<PlusIcon />}
                      onClick={handleCreateCommunity}
                      sx={{ mt: 2 }}
                    >
                      Create Community
                    </Button>
                  )}
                </Box>
              ) : (
                <>
                  {hasCommunities && (
                    <>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: 'secondary.900',
                          mb: { xs: 2, sm: 3 },
                          fontSize: { xs: '1.125rem', sm: '1.25rem' },
                        }}
                      >
                        Communities
                      </Typography>
                      <Grid container spacing={{ xs: 2, sm: 3 }}>
                        {filteredCommunities.map(community => (
                          <Grid item xs={12} sm={6} md={4} key={community.id}>
                            <Card
                              sx={{
                                height: '100%',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'background.paper',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: theme.shadows[4],
                                  borderColor: 'primary.main',
                                },
                              }}
                              onClick={() => handleViewCommunity(community.id)}
                            >
                              <CardContent
                                sx={{
                                  flex: 1,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  p: { xs: 2, sm: 2.5 },
                                }}
                              >
                                <Stack spacing={{ xs: 1.5, sm: 2 }} sx={{ flex: 1 }}>
                                  {/* Community Header */}
                                  <Stack
                                    direction="row"
                                    spacing={{ xs: 1.5, sm: 2 }}
                                    alignItems="flex-start"
                                  >
                                    <Avatar {...getAvatarProps(community.name)} />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography
                                        variant="h6"
                                        component="h3"
                                        sx={{
                                          fontWeight: 600,
                                          fontSize: { xs: '1rem', sm: '1.125rem' },
                                        }}
                                        gutterBottom
                                      >
                                        {community.name}
                                      </Typography>
                                      <Stack
                                        direction="row"
                                        spacing={1}
                                        sx={{ mb: 1 }}
                                        flexWrap="wrap"
                                        gap={0.5}
                                      >
                                        <Chip
                                          label={community.is_active ? 'Active' : 'Inactive'}
                                          color={community.is_active ? 'success' : 'default'}
                                          size="small"
                                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                        />
                                        <Chip
                                          label={
                                            community.user_role === 'ADMIN' ? 'Admin' : 'Member'
                                          }
                                          color={
                                            community.user_role === 'ADMIN' ? 'primary' : 'info'
                                          }
                                          size="small"
                                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                        />
                                      </Stack>
                                    </Box>
                                  </Stack>

                                  {/* Description */}
                                  {community.description && (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{
                                        flex: 1,
                                        display: '-webkit-box',
                                        WebkitLineClamp: 3,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        lineHeight: 1.5,
                                        fontSize: { xs: '0.875rem', sm: '1rem' },
                                      }}
                                    >
                                      {community.description}
                                    </Typography>
                                  )}

                                  {/* Stats */}
                                  <Stack direction="column" spacing={{ xs: 1.5, sm: 2 }}>
                                    <Stack direction="row" alignItems="center" spacing={1.5}>
                                      <Box
                                        sx={{
                                          width: { xs: 32, sm: 36 },
                                          height: { xs: 32, sm: 36 },
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          borderRadius: 2,
                                          bgcolor: alpha('#9D1500', 0.1),
                                          color: 'primary.main',
                                          '& svg': {
                                            display: 'block',
                                          },
                                        }}
                                      >
                                        <Users02Icon width={12} height={12} />
                                      </Box>
                                      <Stack direction="row" alignItems="baseline" spacing={0.5}>
                                        <Typography
                                          variant="body2"
                                          color="secondary.600"
                                          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                        >
                                          {community.member_count}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="secondary.600"
                                          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                        >
                                          {community.member_count === 1 ? 'Member' : 'Members'}
                                        </Typography>
                                      </Stack>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1.5}>
                                      <Box
                                        sx={{
                                          width: { xs: 32, sm: 36 },
                                          height: { xs: 32, sm: 36 },
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          borderRadius: 2,
                                          bgcolor: alpha('#9D1500', 0.1),
                                          color: 'primary.main',
                                          '& svg': {
                                            display: 'block',
                                          },
                                        }}
                                      >
                                        <CalendarIcon width={23} height={23} />
                                      </Box>
                                      <Box sx={{ minWidth: 'fit-content' }}>
                                        <Typography
                                          variant="body2"
                                          color="secondary.600"
                                          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                        >
                                          {formatDate(community.created_at)}
                                        </Typography>
                                      </Box>
                                    </Stack>
                                  </Stack>

                                  {/* View Community Button */}
                                  <Button
                                    variant="outlined"
                                    onClick={e => {
                                      e.stopPropagation();
                                      handleViewCommunity(community.id);
                                    }}
                                    fullWidth
                                    sx={{
                                      mt: 'auto',
                                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                      py: { xs: 0.75, sm: 1 },
                                    }}
                                  >
                                    View Community
                                  </Button>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  )}
                  {showDivider && <Divider sx={{ my: { xs: 3, sm: 4 }, borderColor: 'divider' }} />}
                  {/* Available Communities Section */}
                  {(hasAvailableCommunities || publicCommunitiesLoading) && (
                    <>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: 'secondary.900',
                          mb: { xs: 2, sm: 3 },
                          fontSize: { xs: '1.125rem', sm: '1.25rem' },
                        }}
                      >
                        Available Communities to Join
                      </Typography>
                      {publicCommunitiesLoading ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            Loading available communities...
                          </Typography>
                        </Box>
                      ) : (
                        <Grid container spacing={{ xs: 2, sm: 3 }}>
                          {filteredAvailableCommunities.map((community) => (
                            <Grid item xs={12} sm={6} md={4} key={community.id}>
                              <Card
                                sx={{
                                  height: '100%',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  '&:hover': {
                                    boxShadow: 2,
                                  },
                                }}
                              >
                                <CardContent
                                  sx={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    p: { xs: 2, sm: 2.5 },
                                  }}
                                >
                                  <Stack spacing={{ xs: 1.5, sm: 2 }} sx={{ flex: 1 }}>
                                    {/* Community Header */}
                                    <Stack
                                      direction="row"
                                      spacing={{ xs: 1.5, sm: 2 }}
                                      alignItems="flex-start"
                                    >
                                      <Avatar {...getAvatarProps(community.name)} />
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                          variant="h6"
                                          component="h3"
                                          sx={{
                                            fontWeight: 600,
                                            fontSize: { xs: '1rem', sm: '1.125rem' },
                                          }}
                                          gutterBottom
                                        >
                                          {community.name}
                                        </Typography>
                                        <Stack
                                          direction="row"
                                          spacing={1}
                                          sx={{ mb: 1 }}
                                          flexWrap="wrap"
                                          gap={0.5}
                                        >
                                          <Chip
                                            label={community.is_active ? 'Active' : 'Inactive'}
                                            color={community.is_active ? 'success' : 'default'}
                                            size="small"
                                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                          />
                                          {community.require_verification && (
                                            <Chip
                                              label="Verification Required"
                                              color="warning"
                                              size="small"
                                              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                            />
                                          )}
                                        </Stack>
                                      </Box>
                                    </Stack>

                                    {/* Description */}
                                    {community.description && (
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                          flex: 1,
                                          display: '-webkit-box',
                                          WebkitLineClamp: 3,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden',
                                          lineHeight: 1.5,
                                          fontSize: { xs: '0.875rem', sm: '1rem' },
                                        }}
                                      >
                                        {community.description}
                                      </Typography>
                                    )}

                                    {/* Stats */}
                                    <Stack direction="column" spacing={{ xs: 1.5, sm: 2 }}>
                                      <Stack direction="row" alignItems="center" spacing={1.5}>
                                        <Box
                                          sx={{
                                            width: { xs: 32, sm: 36 },
                                            height: { xs: 32, sm: 36 },
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: 2,
                                            bgcolor: alpha('#9D1500', 0.1),
                                            color: 'primary.main',
                                            '& svg': {
                                              display: 'block',
                                            },
                                          }}
                                        >
                                          <Users02Icon width={12} height={12} />
                                        </Box>
                                        <Stack direction="row" alignItems="baseline" spacing={0.5}>
                                          <Typography
                                            variant="body2"
                                            color="secondary.600"
                                            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                          >
                                            {community.member_count}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            color="secondary.600"
                                            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                          >
                                            {community.member_count === 1 ? 'Member' : 'Members'}
                                          </Typography>
                                        </Stack>
                                      </Stack>
                                      <Stack direction="row" alignItems="center" spacing={1.5}>
                                        <Box
                                          sx={{
                                            width: { xs: 32, sm: 36 },
                                            height: { xs: 32, sm: 36 },
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: 2,
                                            bgcolor: alpha('#9D1500', 0.1),
                                            color: 'primary.main',
                                            '& svg': {
                                              display: 'block',
                                            },
                                          }}
                                        >
                                          <CalendarIcon width={23} height={23} />
                                        </Box>
                                        <Box sx={{ minWidth: 'fit-content' }}>
                                          <Typography
                                            variant="body2"
                                            color="secondary.600"
                                            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                          >
                                            {formatDate(community.created_at)}
                                          </Typography>
                                        </Box>
                                      </Stack>
                                    </Stack>

                                    {/* Join Button */}
                                    <Button
                                      variant="contained"
                                      startIcon={
                                        community.user_membership_status !== 'PENDING' && (
                                          <UserPlus01Icon />
                                        )
                                      }
                                      onClick={() => handleJoinCommunity(community.id)}
                                      disabled={
                                        joiningCommunityId === community.id ||
                                        joinCommunityMutation.isPending ||
                                        community.user_membership_status === 'PENDING'
                                      }
                                      fullWidth
                                      sx={{
                                        mt: 'auto',
                                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                        py: { xs: 0.75, sm: 1 },
                                        ...(community.user_membership_status === 'PENDING' && {
                                          bgcolor: 'warning.main',
                                          '&:hover': {
                                            bgcolor: 'warning.main',
                                          },
                                          '&:disabled': {
                                            bgcolor: 'warning.main',
                                            color: 'white',
                                          },
                                        }),
                                      }}
                                    >
                                      {(() => {
                                        if (joiningCommunityId === community.id) {
                                          return 'Joining...';
                                        }
                                        if (community.user_membership_status === 'PENDING') {
                                          return 'Pending';
                                        }
                                        return 'Join Community';
                                      })()}
                                    </Button>
                                  </Stack>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </>
                  )}
                </>
              )}
            </Box>
          </Paper>

          {/* Dropdown Menu */}
          <Menu
            anchorEl={menuState.anchorEl}
            open={Boolean(menuState.anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleViewFromMenu}>
              <ListItemIcon>
                <EyeIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>View Community</ListItemText>
            </MenuItem>
            {/* Only show leave option if user is not the creator */}
            {(() => {
              const community = filteredCommunities.find(c => c.id === menuState.communityId);
              const isCreator = community?.created_by === session?.user?.id;
              return !isCreator ? (
                <MenuItem onClick={handleLeaveCommunity} sx={{ color: 'error.main' }}>
                  <ListItemIcon>
                    <LogOut01Icon fontSize="small" sx={{ color: 'error.main' }} />
                  </ListItemIcon>
                  <ListItemText>Leave Community</ListItemText>
                </MenuItem>
              ) : null;
            })()}
          </Menu>

          {/* Create Community Modal */}
          {createModalOpen && (
            <CreateCommunityModal
              onClose={handleCreateModalClose}
              onCommunityCreated={handleCommunityCreated}
            />
          )}

          {/* Leave Community Confirmation Dialog */}
          <Dialog
            open={leaveConfirmDialogOpen}
            onClose={() => {
              setLeaveConfirmDialogOpen(false);
              handleMenuClose();
            }}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                p: 1,
              },
            }}
          >
            <DialogTitle
              sx={{
                fontWeight: 600,
                fontSize: '1.5rem',
                color: 'secondary.900',
                pb: 1,
              }}
            >
              Leave Community
            </DialogTitle>
            <DialogContent sx={{ pb: 2 }}>
              <Typography variant="body1" sx={{ mb: 2, color: 'secondary.700' }}>
                Are you sure you want to leave <strong>{menuState.communityName}</strong>?
              </Typography>
              <Typography variant="body2" color="secondary.600">
                You will no longer have access to community activities, discussions, and group
                negotiations. You can rejoin later if the community is still active.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button
                onClick={() => {
                  setLeaveConfirmDialogOpen(false);
                  handleMenuClose();
                }}
                sx={{
                  color: 'secondary.600',
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: 'secondary.100',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmLeave}
                disabled={leaveCommunityMutation.isPending}
                variant="contained"
                sx={{
                  bgcolor: '#f44336',
                  color: 'white',
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: '0px 2px 8px rgba(244, 67, 54, 0.2)',
                  '&:hover': {
                    bgcolor: '#d32f2f',
                    boxShadow: '0px 4px 12px rgba(244, 67, 54, 0.3)',
                  },
                  '&:disabled': {
                    bgcolor: 'secondary.300',
                    color: 'secondary.500',
                  },
                }}
              >
                {leaveCommunityMutation.isPending ? 'Leaving...' : 'Leave Community'}
              </Button>
            </DialogActions>
          </Dialog>
        </Stack>
      </Container>
    </Box>
  );
};

export default MyCommunitiesPage;
